import {
    getSegmentationPlayerTags,
    isShownByScheduler,
    isShownByLogin,
    isShownBySegmentation
} from 'js/utils/segmentation/segmentation.util';

import * as Types from './types';
import { IBannerSlide } from '../../types';

export const getSliderOptions: Types.IGetSliderOptions = ({ bannerFeed, formFactor }) => {
    const feedConfiguration = bannerFeed.bannerFeedConfiguration[formFactor];

    return {
        height: `${feedConfiguration.bannerHeight}${feedConfiguration.bannerHeightUnits}`,
        animation: {
            displayTime: Number(feedConfiguration.slideAnimationTime) || 0,
            type: feedConfiguration.slideAnimationType || 'fade'
        },
        controls: {
            arrows: {
                enabled: feedConfiguration.isArrowsControlsEnabled,
                position: `${feedConfiguration.arrowsControlsPosition || '0'}${feedConfiguration.arrowsControlsUnits}`
            },
            bullets: {
                enabled: feedConfiguration.isBulletsControlsEnabled,
                position: {
                    offsetX: Number(feedConfiguration.bulletsControlsXOffset) || 0,
                    offsetY: Number(feedConfiguration.bulletsControlsYOffset) || 0,
                    x: feedConfiguration.bulletsControlsXPosition || 'center',
                    y: feedConfiguration.bulletsControlsYPosition || 'bottom'
                }
            },
            progressBar: {
                enabled: feedConfiguration.isProgressBarControlsEnabled,
                position: feedConfiguration.progressBarControlsPosition
            },
            buttons: {
                enabled: false
            }
        }
    };
};

export const getSliderPlayerTags: Types.IGetSliderPlayerTags = async ({ bannerFeed, isLoggedIn, getPlayerTags }) => {
    const playerTags: string[] = [];
    const feedContent = bannerFeed.bannerFeedContent || [];

    if (!feedContent.length || !isLoggedIn) {
        return [];
    }

    feedContent.forEach((slideGroup) => {
        slideGroup.children.forEach((slide) => {
            const segmentationSettings = slide.isCustomSegmentationEnabled
                ? slide.loggedInSegmentation
                : slideGroup.loggedInSegmentation;

            const slideSegmentationPlayerTags = getSegmentationPlayerTags(segmentationSettings);

            slideSegmentationPlayerTags.forEach((item) => playerTags.push(item));
        });
    });

    if (playerTags.length) {
        const { tags = [] } = await getPlayerTags({ tagName: playerTags.join(',') });

        return tags;
    }

    return [];
};

export const isSlideShownByActive: Types.IIsSlideShownByActive = ({ slide, slideGroup, formFactor }) => {
    return slideGroup.isActive && slide[formFactor].isActive;
};

const isSlideShownByScheduler: Types.IIsSlideShownByScheduler = ({ slide, slideGroup }) => {
    const schedulerSettings = slide.isCustomSchedulerEnabled ? slide.scheduler : slideGroup.scheduler;
    const schedulerTimeZone = slide.isCustomSchedulerEnabled ? slide.schedulerTimeZone : slideGroup.schedulerTimeZone;

    return isShownByScheduler({ scheduler: schedulerSettings, schedulerTimeZone });
};

export const isSlideShownByLogin: Types.IIsSlideShownByLogin = ({ slide, slideGroup, isLoggedIn }) => {
    const isEnabledForGuest = slide.isCustomSegmentationEnabled ? slide.isEnabledForGuest : slideGroup.isEnabledForGuest;
    const isEnabledForLoggedIn = slide.isCustomSegmentationEnabled ? slide.isEnabledForLoggedIn : slideGroup.isEnabledForLoggedIn;

    return isShownByLogin({ isEnabledForGuest, isEnabledForLoggedIn, isLoggedIn });
};

export const isSlideShownBySegmentation: Types.IIsSlideShownBySegmentation = ({ slide, slideGroup, options }) => {
    const loggedInSegmentation = slide.isCustomSegmentationEnabled ? slide.loggedInSegmentation : slideGroup.loggedInSegmentation;
    const guestSegmentation = slide.isCustomSegmentationEnabled ? slide.guestSegmentation : slideGroup.guestSegmentation;

    return isShownBySegmentation({ loggedInSegmentation, guestSegmentation, options });
};

export const getSlides: Types.IGetSlides = ({ bannerFeed, options }) => {
    const slides: IBannerSlide[] = [];
    const { isLoggedIn, formFactor } = options;
    const feedContent = bannerFeed.bannerFeedContent || [];

    if (!feedContent.length) {
        return slides;
    }

    feedContent.forEach((slideGroup) => {
        slideGroup.children.forEach((slide) => {
            if (
                isSlideShownByActive({ slide, slideGroup, formFactor }) &&
                isSlideShownByScheduler({ slide, slideGroup }) &&
                isSlideShownByLogin({ slide, slideGroup, isLoggedIn }) &&
                isSlideShownBySegmentation({ slide, slideGroup, options })
            ) {
                slides.push({
                    id: slide.id,
                    name: slide.name,
                    ...slide[formFactor]
                });
            }
        });
    });

    return slides;
};
